import { useEffect, useState } from 'react';

/**
 * The mobile shell (top bar plus navigation drawer) renders at or below 640px, and on a
 * touch-first screen at or below 760px. A phone held landscape keeps it, while a half-screen window
 * on a 13-14" laptop (735-756px) keeps the desktop shell. The shell blocks in forge-theme.css and
 * the toast offset in index.css use the same query:
 * `(max-width: 640px), (max-width: 760px) and (pointer: coarse)`.
 */
export const MOBILE_BREAKPOINT = 640;
const TOUCH_MOBILE_BREAKPOINT = 760;

const readIsMobile = () => {
  if (typeof window === 'undefined') return false;
  if (window.innerWidth <= MOBILE_BREAKPOINT) return true;
  return (
    window.innerWidth <= TOUCH_MOBILE_BREAKPOINT &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches
  );
};

/** Shared by the Forge, Eval, and Archon layouts so all three shells switch at the same width. */
export function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(readIsMobile);

  useEffect(() => {
    const update = () => setIsMobile(readIsMobile());
    // The primary pointer can change without a resize (a tablet's keyboard detached), and the
    // CSS query follows it at once.
    const pointerQuery =
      typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : null;
    window.addEventListener('resize', update);
    pointerQuery?.addEventListener('change', update);
    return () => {
      window.removeEventListener('resize', update);
      pointerQuery?.removeEventListener('change', update);
    };
  }, []);

  return isMobile;
}
